import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Alert } from "reactstrap";
import { Select, Input, Modal, Button } from "antd";
import { tasksApis } from "../../../api/NewTasks";
import Swal from "sweetalert2";
import { EditAccess } from "../../../components/accessControl/AccessControl";

interface ChecklistModalProps {
  open: boolean;
  checklistId: number | null;
  checklistLinkId?: number | null;
  onClose: () => void;
  onSave?: (success: boolean) => void;
  extraFooter?: React.ReactNode;
}

const TaskCheckListCreateModal: React.FC<ChecklistModalProps> = ({
  open,
  checklistId,
  checklistLinkId,
  onClose,
  onSave,
  extraFooter,
}) => {
  const methods = useForm();
  const [answers, setAnswers] = React.useState<any>({});
  const [loading, setLoading] = useState(false);
  const [checklist, setChecklist] = useState<any>(null);
  const [missingRequired, setMissingRequired] = useState(false);

  useEffect(() => {
    if (open && checklistId) {
      setLoading(true);
      tasksApis
        .fetchCheckListById(checklistId)
        .then((response: any) => {
          if (response?.status) {
            setChecklist(response.data);
          } else {
            setChecklist(null);
          }
        })
        .catch(() => setChecklist(null))
        .finally(() => setLoading(false));
    } else {
      setChecklist(null);
    }
    setAnswers({});
    methods.reset();
    setMissingRequired(false);
    // eslint-disable-next-line
  }, [checklistId, open, checklistLinkId]);

  const handleAnswerChange = (questionId: number, value: any) => {
    setAnswers((prev: any) => ({ ...prev, [questionId]: value }));
    methods.setValue(`answers.${questionId}`, value);
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      const requiredQuestions =
        checklist?.checklistQuestions?.filter((q: any) => q.isMandatory) || [];

      const missingRequiredQs = requiredQuestions.filter(
        (q: any) =>
          !answers[q.questionId] ||
          (Array.isArray(answers[q.questionId]) &&
            answers[q.questionId].length === 0)
      );

      if (missingRequiredQs.length > 0) {
        // Show error for missing required fields
        setMissingRequired(true);
        return;
      }
      setMissingRequired(false);

      // Format answers as per API payload DTO
      const formattedAnswers =
        checklist?.checklistQuestions?.map((question: any) => {
          let answerValue = answers[question.questionId];

          // Format answer basesd on question type
          if (
            question.questionType === "multiple_choice" &&
            Array.isArray(answerValue)
          ) {
            answerValue = answerValue.join(",");
          }

          return {
            questionId: question.questionId,
            answer: answerValue || "",
            remark: "", // Optional remark field
          };
        }) || [];

      // Prepare payload
      const payload = {
        checklistLinkId: checklistLinkId,
        checklistId: checklistId,
        answers: formattedAnswers,
      };

      // Submit to API
      const response = await tasksApis.submitChecklistAnswers(payload);

      if (response?.status) {
        if (onSave) {
          onSave(true);
        }
      } else {
        // Handle error - call parent onSave with failure flag
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to submit checklist answers.",
          confirmButtonText: "Ok",
        });
        if (onSave) {
          onSave(false);
        }
      }
    } catch (error) {
      console.error("Error submitting checklist answers:", error);
    }
  };

  const renderPreviewQuestion = (question: any) => {
    if (question.questionType === "text") {
      return (
        <Input
          {...methods.register(`answers.${question.questionId}`, {
            required: question.isMandatory,
          })}
          placeholder={question.description || "Enter answer"}
          style={{ width: "100%" }}
          value={answers[question.questionId] || ""}
          onChange={(e) =>
            handleAnswerChange(question.questionId, e.target.value)
          }
        />
      );
    }
    if (question.questionType === "single_choice") {
      return (
        <Select
          style={{ width: "100%" }}
          placeholder={question.description || "Select option"}
          options={
            question.optionsType
              ? Object.entries(question.optionsType).map(([key, val]) => ({
                  label: typeof val === "string" ? val.replace(/"/g, "") : val,
                  value: key,
                }))
              : []
          }
          value={answers[question.questionId] || undefined}
          onChange={(value) => handleAnswerChange(question.questionId, value)}
        />
      );
    }
    if (question.questionType === "multiple_choice") {
      return (
        <Select
          mode="multiple"
          style={{ width: "100%" }}
          placeholder={question.description || "Select options"}
          options={
            question.optionsType
              ? Object.entries(question.optionsType).map(([key, val]) => ({
                  label: typeof val === "string" ? val.replace(/"/g, "") : val,
                  value: key,
                }))
              : []
          }
          value={answers[question.questionId] || []}
          onChange={(value) => handleAnswerChange(question.questionId, value)}
        />
      );
    }
    return null;
  };

  return (
    <Modal
      title={""}
      open={open}
      onCancel={onClose}
      footer={
        extraFooter !== undefined
          ? extraFooter
          : [
              <EditAccess moduleName="Tasks" key="save">
                <Button type="primary" onClick={handleSave}>
                  Submit
                </Button>
              </EditAccess>,
            ]
      }
      width={700}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <span>Loading...</span>
        </div>
      ) : checklist ? (
        <FormProvider {...methods}>
          <form>
            <div className="mt-0 p-4">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h3
                    style={{
                      marginTop: "-20px",
                      marginBottom: "6px",
                      fontSize: "25px",
                    }}
                  >
                    {checklist.checklistName}
                  </h3>
                  <div className="mt-1">
                    <p className="text-soft mb-1">
                      Created by : {checklist.createdByUserName || "Admin"}
                    </p>
                    <p className="text-soft">
                      {checklist.checklistQuestions?.length || 0} Check Points
                    </p>
                  </div>
                </div>
              </div>

              {checklist.checklistQuestions?.length === 0 ? (
                <Alert color="info">No questions have been added yet.</Alert>
              ) : (
                <div className="preview-checklist">
                  {missingRequired && (
                    <Alert color="danger" style={{ marginBottom: 16 }}>
                      Please fill required fields.
                    </Alert>
                  )}
                  <table className="table table-bordered w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th
                          className="p-2 text-left font-semibold text-gray-900"
                          style={{ width: "30%", fontSize: "14px" }}
                        >
                          Question
                        </th>
                        <th
                          className="p-2 text-left font-semibold text-gray-900"
                          style={{ width: "30%", fontSize: "14px" }}
                        >
                          Responses
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {checklist.checklistQuestions?.map(
                        (question: any, index: number) => (
                          <tr
                            key={question.questionId || index}
                            className="border-t"
                          >
                            <td
                              className="p-2 text-gray-900 font-medium"
                              style={{ wordBreak: "break-word" }}
                            >
                              <div
                                className="text-black font-semibold d-flex align-items-center"
                                style={{ fontSize: "14px" }}
                              >
                                <span
                                  className="text-danger"
                                  style={{
                                    display: "inline-block",
                                    width: "12px",
                                    marginRight: "4px",
                                    textAlign: "center",
                                  }}
                                >
                                  {question.isMandatory ? "*" : ""}
                                </span>
                                {question.questionName || question.name}
                              </div>
                              {question.description && (
                                <div className="text-gray-800 mt-1">
                                  {question.description ||
                                    "No description provided"}
                                </div>
                              )}
                            </td>
                            <td className="p-2 text-gray-900 font-medium">
                              {renderPreviewQuestion(question)}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </form>
        </FormProvider>
      ) : null}
    </Modal>
  );
};

export default TaskCheckListCreateModal;
